import type { ReactNode } from 'react';
import { Box, Button, Collapse, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';

export interface BrandingRowProps {
  /** Row label — "Document logo", "Document font". */
  name: string;
  /** The current value, read at a glance. A node so a row can show swatches. */
  detail: ReactNode;
  /** Whether the org has overridden this asset, vs running on the default. */
  isSet: boolean;
  /** Whether this row's editor is expanded. */
  open: boolean;
  onToggle: () => void;
  /** The editor. Rendered only while open. */
  children: ReactNode;
  testId?: string;
}

/**
 * One branding asset, as a row: state, name, current value, and the control
 * that edits it — in that order, on that row.
 *
 * The previous card put each asset's action button in the card header, so two
 * "Choose…" buttons sat stacked top-right with nothing tying either to the
 * logo it uploaded. Keeping the action on the row is the whole point of this
 * component; resist moving it back out to a toolbar.
 *
 * The action's LABEL follows `isSet`, because "Edit" on an asset that has never
 * been configured reads as though something already exists to edit.
 */
export function BrandingRow({
  name,
  detail,
  isSet,
  open,
  onToggle,
  children,
  testId,
}: BrandingRowProps) {
  return (
    <Box
      data-testid={testId}
      sx={{
        border: '1px solid',
        borderColor: open ? 'primary.main' : '#e2e8f0',
        borderRadius: 1,
        mb: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: 1.25,
          bgcolor: open ? 'action.hover' : 'transparent',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 26,
            height: 26,
            flex: 'none',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isSet ? 'success.light' : 'action.selected',
            color: isSet ? 'success.dark' : 'text.disabled',
          }}
        >
          {isSet ? <CheckIcon sx={{ fontSize: 15 }} /> : <RemoveIcon sx={{ fontSize: 15 }} />}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight={500}>
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {detail}
          </Typography>
        </Box>

        <Button size="small" onClick={onToggle}>
          {open ? 'Close' : isSet ? 'Edit' : 'Set up'}
        </Button>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: 1.5, pb: 2, pt: 0.5, borderTop: '1px solid #e2e8f0' }}>{children}</Box>
      </Collapse>
    </Box>
  );
}

export default BrandingRow;
